/**
 * AffiliateAttributionCapture
 *
 * Mounted once at the App root. Watches the URL for ``?ref=CODE`` and:
 *   1. Records a server-side click via POST /api/affiliate/track-click.
 *   2. Stores the resulting {click_id, code, ts} in localStorage under
 *      ``postmoro_aff_ref`` for up to 60 days (attribution window).
 *
 * Separately: when the AuthProvider reports an authenticated user, we
 * attempt ONE POST /api/affiliate/attribute-signup with the stored
 * click_id. On success (or "already_attributed") we drop the local
 * record. Attribution is FIRST-VALID-LOCKED server-side, so calling
 * this again on later logins is safe (server no-op).
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";

const STORAGE_KEY = "postmoro_aff_ref";
const MAX_AGE_MS = 60 * 24 * 60 * 60 * 1000; // 60 days

function readStored() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.ts) return null;
    if (Date.now() - parsed.ts > MAX_AGE_MS) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeStored(payload) {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...payload, ts: Date.now() }),
    );
  } catch {
    /* localStorage disabled — silently skip. */
  }
}

function clearStored() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* no-op */
  }
}

export default function AffiliateAttributionCapture() {
  const location = useLocation();
  const { user } = useAuth();
  const claimedRef = useRef(false);

  // 1) URL ?ref= capture -> click record
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = (params.get("ref") || "").trim().toLowerCase();
    if (!code) return;
    const existing = readStored();
    if (existing && existing.code === code) return; // already captured
    // Fire-and-forget.
    api
      .post("/affiliate/track-click", {
        code,
        session_id: window.sessionStorage?.getItem("postmoro_sid") || null,
        landing_page: window.location.pathname + window.location.search,
        utm_source: params.get("utm_source") || null,
        utm_medium: params.get("utm_medium") || null,
        utm_campaign: params.get("utm_campaign") || null,
      })
      .then((resp) => {
        const d = resp?.data;
        if (d?.tracked) {
          writeStored({ click_id: d.click_id, code: d.affiliate_code });
        }
      })
      .catch(() => {
        /* Silent — attribution failures never surface to the visitor. */
      });
  }, [location.pathname, location.search]);

  // 2) After login: attribute the referral to the user.
  useEffect(() => {
    if (!user?.user_id) return;
    if (claimedRef.current) return;
    const stored = readStored();
    if (!stored) return;
    claimedRef.current = true;
    api
      .post("/affiliate/attribute-signup", {
        click_id: stored.click_id || null,
        code: stored.code || null,
      })
      .then((resp) => {
        // Whether attached or already-attributed, we no longer need
        // the local record; the server has the authoritative
        // referral row.
        if (resp?.data?.attached || resp?.data?.reason === "already_attributed") {
          clearStored();
        }
      })
      .catch(() => {
        /* Silent — retry on next mount if it fails now. */
      });
  }, [user?.user_id]);

  return null;
}
